/**
 * Shared presentation bits for the visitor module — status labels, badge
 * colours, the scan-refusal copy, and the date/time formatters.
 *
 * Extracted because the dashboard, the register, the scanner and the detail
 * dialog all render the same statuses, and a status that is amber in one place
 * and grey in another is how a desk learns to distrust the screen.
 */
import { format, parseISO } from "date-fns";
import type { ScanResult, Visitor, VisitorStatus } from "../../api/visitors";

export const STATUS_LABEL: Record<VisitorStatus, string> = {
  INVITED: "Invited",
  CHECKED_IN: "Checked In",
  CHECKED_OUT: "Checked Out",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
};

/** Badge classes. Green = on the premises, which is the one a desk scans for. */
export const STATUS_BADGE: Record<VisitorStatus, string> = {
  INVITED: "bg-blue-100 text-blue-700",
  CHECKED_IN: "bg-green-100 text-green-700",
  CHECKED_OUT: "bg-gray-100 text-gray-600",
  CANCELLED: "bg-red-100 text-red-700",
  EXPIRED: "bg-amber-100 text-amber-700",
};

export const STATUS_ORDER: VisitorStatus[] = [
  "INVITED",
  "CHECKED_IN",
  "CHECKED_OUT",
  "CANCELLED",
  "EXPIRED",
];

/**
 * What the desk should DO about a refusal. The backend already supplies a
 * sentence; this adds the next step, which is what the person at the counter
 * actually needs ("wait" vs "ask the host to re-invite").
 */
export const SCAN_RESULT_HINT: Record<ScanResult, string> = {
  OK: "",
  NOT_FOUND:
    "This code is not a visitor pass for this office. Check the pass or register the visitor as a walk-in.",
  NOT_YET_VALID:
    "The visit is scheduled for a later date — the pass starts working on the day of the visit.",
  EXPIRED: "The pass has expired. The host needs to issue a new invitation.",
  CANCELLED: "This visit was cancelled. Confirm with the host before allowing entry.",
  ALREADY_IN:
    "This visitor is already inside — scan again on the way out to check them out.",
  ALREADY_OUT:
    "This pass has already been used for entry and exit. A fresh invitation is needed.",
  NOT_IN: "Check the visitor in first, then scan again when they leave.",
};

/** Red for a security refusal, amber for a timing/sequence one. */
export const SCAN_RESULT_TONE: Record<ScanResult, "ok" | "warn" | "deny"> = {
  OK: "ok",
  NOT_FOUND: "deny",
  CANCELLED: "deny",
  EXPIRED: "deny",
  NOT_YET_VALID: "warn",
  ALREADY_IN: "warn",
  ALREADY_OUT: "warn",
  NOT_IN: "warn",
};

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd MMM yyyy");
  } catch {
    return String(value);
  }
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd MMM yyyy, h:mm a");
  } catch {
    return String(value);
  }
}

export function fmtTimeOnly(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return format(parseISO(value), "h:mm a");
  } catch {
    return String(value);
  }
}

/** "09:30" → "9:30 AM". The scheduled time is a bare string, not a timestamp. */
export function fmtClock(hhmm: string | null | undefined): string {
  if (!hhmm) return "—";
  const [h, m] = String(hhmm).split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return String(hhmm);
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m ?? "00"} ${suffix}`;
}

/** Initials for the avatar placeholder — a visitor has no employee photo. */
export function initials(name: string | null | undefined): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : ""))
    .toUpperCase();
}

/** How long a visitor has been (or was) on site, e.g. "1h 12m". */
export function visitDuration(v: Visitor): string | null {
  if (!v.checked_in_at) return null;
  try {
    const start = parseISO(v.checked_in_at).getTime();
    const end = v.checked_out_at ? parseISO(v.checked_out_at).getTime() : Date.now();
    const mins = Math.max(0, Math.round((end - start) / 60000));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h ? `${h}h ${m}m` : `${m}m`;
  } catch {
    return null;
  }
}

/**
 * FastAPI 422s return `detail` as an array of objects, and the visitor
 * endpoints return it as an OBJECT for a scan refusal — never hand either to a
 * toast or to JSX directly (mirrors the Expenses/Assets page helper).
 */
export function errMsg(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (d && typeof d === "object" && !Array.isArray(d) && typeof d.message === "string") {
    return d.message;
  }
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => (x && typeof x === "object" ? x.msg : typeof x === "string" ? x : null))
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

/** Today as "yyyy-MM-dd" in LOCAL time — `toISOString()` would shift it. */
export function todayISO(): string {
  return format(new Date(), "yyyy-MM-dd");
}
